// The agent writes `[[shift:<uuid>]]`-style tokens instead of names; the
// chat turns each one into a badge that carries the name and links to the
// record. Parsing is pure so both plain text and Markdown paths share it.
export const REFERENCE_TYPES = [
    'shift',
    'caregiver',
    'client',
    'callout',
    'group',
    'playbook',
    'care_plan',
    'caregiver_memory',
    'client_memory',
] as const;
export type ReferenceType = (typeof REFERENCE_TYPES)[number];

// A source citation names why a decision was made; the other types name who
// or what is being discussed. Badges render the two differently.
const SOURCE_CITATION_TYPES: ReadonlySet<string> = new Set([
    'playbook',
    'care_plan',
    'caregiver_memory',
    'client_memory',
]);

export function isSourceCitationType(type: ReferenceType): boolean {
    return SOURCE_CITATION_TYPES.has(type);
}

export type ReferenceSegment =
    | Readonly<{ readonly kind: 'text'; readonly text: string }>
    | Readonly<{
          readonly kind: 'reference';
          readonly type: ReferenceType;
          readonly id: string;
          /** The model's own words, when it wrapped them in the token. */
          readonly phrase: string | null;
      }>;

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const UUID_PATTERN = new RegExp(`^${UUID}$`, 'iu');

export function isUuid(value: string): boolean {
    return UUID_PATTERN.test(value);
}
// A source the model wrapped around its own words carries them after a
// pipe. Excluding ] | and newline keeps a phrase from running into the
// next token, and keeps the match linear.
const PHRASE = '(?:\\|([^\\]|\\n]{1,80}))?';
const TOKEN_PATTERN = new RegExp(
    `\\[\\[(${REFERENCE_TYPES.join('|')}):(${UUID})${PHRASE}\\]\\]`,
    'giu',
);
const NESTED_TOKEN_PATTERN = new RegExp(
    `\\[\\[(\\[\\[(?:${REFERENCE_TYPES.join('|')}):${UUID}\\]\\])\\]\\]`,
    'giu',
);
// A relative path: react-markdown drops unknown URL schemes, so a custom
// scheme would reach the anchor renderer as an empty href.
export const REFERENCE_HREF_SCHEME = '/phoebe-ref/';
// Marks a citation the model wrapped around its own words, so the renderer
// prints those words instead of a chip.
const ANCHORED_HREF_FRAGMENT = '#anchored';

const FALLBACK_LABELS: Readonly<Record<ReferenceType, string>> = {
    shift: 'Shift',
    caregiver: 'Caregiver',
    client: 'Client',
    callout: 'Outreach',
    group: 'Shift group',
    playbook: 'Playbook',
    care_plan: 'Care plan',
    caregiver_memory: 'Memory',
    client_memory: 'Memory',
};

function isReferenceType(value: string): value is ReferenceType {
    return (REFERENCE_TYPES as ReadonlyArray<string>).includes(value);
}

function normalizeReferenceTokens(text: string): string {
    return text.replace(NESTED_TOKEN_PATTERN, '$1');
}

export function parseReferenceTokens(
    text: string,
): ReadonlyArray<ReferenceSegment> {
    const normalizedText = normalizeReferenceTokens(text);
    const segments: Array<ReferenceSegment> = [];
    let lastIndex = 0;
    for (const match of normalizedText.matchAll(TOKEN_PATTERN)) {
        const [token, type, id, phrase] = match;
        const start = match.index;
        if (start > lastIndex) {
            segments.push({
                kind: 'text',
                text: normalizedText.slice(lastIndex, start),
            });
        }
        const normalizedType = type?.toLowerCase();
        if (
            normalizedType !== undefined &&
            id !== undefined &&
            isReferenceType(normalizedType)
        ) {
            segments.push({
                kind: 'reference',
                type: normalizedType,
                id: id.toLowerCase(),
                phrase: phrase?.trim() || null,
            });
        }
        lastIndex = start + token.length;
    }
    if (lastIndex < normalizedText.length) {
        segments.push({ kind: 'text', text: normalizedText.slice(lastIndex) });
    }
    return segments;
}

// Escaped, never dropped: parentheses are ordinary prose, and deleting
// them would change the sentence the agent wrote.
const MARKDOWN_SIGNIFICANT = /[[\]()*_`\\]/gu;

function linkSafePhrase(phrase: string | undefined): string | null {
    if (phrase === undefined) {
        return null;
    }
    const collapsed = phrase.replace(/\s+/gu, ' ').trim();
    return collapsed === ''
        ? null
        : collapsed.replace(MARKDOWN_SIGNIFICANT, '\\$&');
}

export function referenceTokensToMarkdown(text: string): string {
    return normalizeReferenceTokens(text).replace(
        TOKEN_PATTERN,
        (token: string, type: string, id: string, phrase?: string) => {
            const normalizedType = type.toLowerCase();
            if (!isReferenceType(normalizedType)) {
                return token;
            }
            const href = `${REFERENCE_HREF_SCHEME}${normalizedType}/${id.toLowerCase()}`;
            const wrapped = linkSafePhrase(phrase);
            // The fragment, not the link text, says which form this is: a
            // phrase can legitimately equal the fallback label.
            return wrapped === null
                ? `[${FALLBACK_LABELS[normalizedType]}](${href})`
                : `[${wrapped}](${href}${ANCHORED_HREF_FRAGMENT})`;
        },
    );
}

export function parseReferenceHref(href: string | undefined): Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
    readonly anchored: boolean;
}> | null {
    if (href === undefined || !href.startsWith(REFERENCE_HREF_SCHEME)) {
        return null;
    }
    const anchored = href.endsWith(ANCHORED_HREF_FRAGMENT);
    const path = anchored
        ? href.slice(0, -ANCHORED_HREF_FRAGMENT.length)
        : href;
    const [type, id] = path.slice(REFERENCE_HREF_SCHEME.length).split('/');
    if (type === undefined || id === undefined || !isReferenceType(type)) {
        return null;
    }
    return { type, id, anchored };
}

export function referenceRecordHref(type: ReferenceType, id: string): string {
    switch (type) {
        case 'shift':
            return `/shifts/${id}`;
        case 'callout':
            return `/outreaches/${id}`;
        // Only until the group resolves: the badge then links to the
        // outreach the resolve response named.
        case 'group':
            return '/outreaches';
        case 'caregiver':
            return `/caregivers?caregiverId=${id}`;
        case 'client':
            return `/clients?clientId=${id}`;
        case 'playbook':
            return `/playbooks?playbook=${id}`;
        // Care plan and memory hang off a person, so these carry person ids.
        case 'care_plan':
        case 'client_memory':
            return `/clients?clientId=${id}`;
        case 'caregiver_memory':
            return `/caregivers?caregiverId=${id}`;
        default: {
            const exhaustiveType: never = type;
            return exhaustiveType;
        }
    }
}

export function referenceFallbackLabel(type: ReferenceType): string {
    return FALLBACK_LABELS[type];
}

const REFERENCE_TYPE_BY_ID_COLUMN = {
    shift_id: 'shift',
    caregiver_id: 'caregiver',
    client_id: 'client',
} as const;

/** The message a table action sends: the picked rows, then its prompt —
 * attached context leads, so the sent bubble shows the pills up top. */
export function selectionActionMessage(
    prompt: string,
    idColumn: keyof typeof REFERENCE_TYPE_BY_ID_COLUMN,
    ids: ReadonlyArray<string>,
): string {
    const type = REFERENCE_TYPE_BY_ID_COLUMN[idColumn];
    return [...ids.map((id) => `[[${type}:${id}]]`), prompt].join(' ').trim();
}
